import type { ReactNode } from "react";

import { Avatar, Chip } from "@heroui/react";

import { DeliveryType } from "@/enums/delivery";

type ChipColor = "default" | "accent" | "success" | "warning" | "danger";

const ORDER_STATUS_COLOR: Record<string, ChipColor> = {
  "awaiting-payment": "warning",
  pending: "default",
  paid: "success",
  packaged: "warning",
  packaging: "warning",
  "en-route": "accent",
  enroute: "accent",
  delivered: "success",
  failed: "danger",
  cancelled: "danger",
};

/** The API's own text; the first letter is capitalised by CSS so the text itself is unchanged. */
const statusText = (status: string) =>
  status.toLowerCase() === "en-route"
    ? "En route"
    : status.replace(/[_-]/g, " ");

/** A soft chip in the status's semantic colour. The label keeps the API's own text. */
export function StatusChip({
  status,
  label,
  color,
}: {
  status: string;
  label?: string;
  color?: ChipColor;
}) {
  return (
    <Chip
      color={color ?? ORDER_STATUS_COLOR[status.toLowerCase()] ?? "default"}
      variant="soft"
    >
      <span className="inline-block whitespace-nowrap first-letter:uppercase">
        {label ?? statusText(status)}
      </span>
    </Chip>
  );
}

const DELIVERY_TYPE_LABEL: Record<DeliveryType, string> = {
  [DeliveryType.EXPRESS]: "Express",
  [DeliveryType.SCHEDULED]: "Scheduled",
};

export function DeliveryTypeChip({ type }: { type: DeliveryType }) {
  return (
    <Chip
      color={type === DeliveryType.EXPRESS ? "accent" : "default"}
      variant="soft"
    >
      {DELIVERY_TYPE_LABEL[type]}
    </Chip>
  );
}

export function paymentChipColor(isPaid: boolean): ChipColor {
  return isPaid ? "success" : "default";
}

const AVATAR_TONES = [
  "bg-accent/15 text-accent",
  "bg-[color-mix(in_oklab,var(--success)_15%,transparent)] text-(--success)",
  "bg-[color-mix(in_oklab,var(--warning)_18%,transparent)] text-(--warning)",
  "bg-[color-mix(in_oklab,var(--danger)_15%,transparent)] text-(--danger)",
  "bg-(--default) text-(--foreground)",
];

function toneFor(seed: string) {
  let hash = 0;

  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;

  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

export function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

/** Initials on a soft tint that stays the same for the same person. */
export function PersonAvatar({
  name,
  size = "sm",
}: {
  name: string;
  size?: "sm" | "md" | "lg";
}) {
  const initials = initialsOf(name);

  return (
    <Avatar size={size}>
      <Avatar.Fallback
        className={initials ? toneFor(name) : undefined}
        delayMs={0}
      >
        {initials || "?"}
      </Avatar.Fallback>
    </Avatar>
  );
}

export function fullName(user: {
  firstName?: string | null;
  lastName?: string | null;
}) {
  return [user.firstName, user.lastName].filter(Boolean).join(" ");
}

/** A person cell: avatar, name and phone, the same on every list. */
export function PersonCell({
  firstName,
  lastName,
  phoneNumber,
}: {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
}) {
  const name = fullName({ firstName, lastName });

  return (
    <div className="flex items-center gap-3">
      <PersonAvatar name={name} />
      <div className="flex min-w-0 flex-col leading-snug">
        <span className={name ? "truncate font-medium" : "text-muted"}>
          {name || "No name"}
        </span>
        <span className="text-muted text-xs tabular-nums">{phoneNumber}</span>
      </div>
    </div>
  );
}

/** The row above a table: search on the left, the page's controls on the right. */
export function Toolbar({
  children,
  end,
}: {
  children: ReactNode;
  end?: ReactNode;
}) {
  return (
    <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-3">
        {children}
      </div>
      {end ? (
        <div className="flex min-w-0 max-w-full items-center gap-2">{end}</div>
      ) : null}
    </div>
  );
}

export const aed = (amount: number | string | null | undefined) =>
  amount === null || amount === undefined || amount === ""
    ? "Not set"
    : `AED ${Number(amount).toFixed(2)}`;

const AED_ROUNDED = new Intl.NumberFormat("en", {
  style: "currency",
  currency: "AED",
  maximumFractionDigits: 0,
});

/** Whole dirhams with thousands separators, for totals: "AED 9,120". */
export const aedRounded = (amount: number) => AED_ROUNDED.format(amount);

const DATE = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

export const formatDay = (value: string | Date) => DATE.format(new Date(value));
export const formatDayTime = (value: string | Date) =>
  DATE_TIME.format(new Date(value)).replace(/ (am|pm)$/i, (m) =>
    m.toUpperCase(),
  );
